import React from "react";
// eslint-disable-next-line
import { MouseEvent, useEffect, useMemo, useState } from "react";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Chip,
  SnackbarProps,
  Stack,
  Tooltip,
  Typography,
  IconButton,
} from "@mui/material";
import _debounce from "lodash/debounce";
// third-party
import { ColumnDef } from "@tanstack/react-table";

import {
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  DeleteOutlined,
  MinusOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import Loader from "../../../../../../components/Loader";
import { FormattedMessage } from "react-intl";
import { USERAXIOS } from "../../../../../../api/userAxios";
import ReusableTable from "../../../../../../components/ReusableComponent/ReusableTable";
import { openSnackbar } from "../../../../../../api/snackbar";
import ReusableDeleteDialog from "../../../../../../components/ReusableComponent/ReusableDeleteDialog";
import ExpandingDetail from "./ExpandingVacancyDetails";
import { VacancyPackage } from "../../../../../../types/orders-managment/packages";
import AddEditVacancyPackage from "./AddEditVacancyPackage";

// ==============================|| COMPANY reportS LIST ||============================== //

const VacanciesPackages = () => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);

  const [customerModal, setCustomerModal] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState<VacancyPackage | null>(null);
  const [selected, setSelected] = useState<any>("");
  const [expandedRowId, setExpandedRowId] = useState<string | number | null>(
    null
  );

  const handleClose = () => {
    setOpen(!open);
  };

  const handleRowExpand = (rowId: string | number) => {
    setExpandedRowId(expandedRowId === rowId ? null : rowId);
  };

  const columns = useMemo<ColumnDef<VacancyPackage>[]>(
    () => [
      {
        header: () => <FormattedMessage id="ID" />,
        accessorKey: "id",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.id}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Type" />,
        accessorKey: "type",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.type}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="fees" />,
        accessorKey: "fees",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.fees}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="count" />,
        accessorKey: "count",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.count}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="price per unit" />,
        accessorKey: "price_per_unit",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.price_per_unit}
          </Typography>
        ),
      },

      {
        header: () => <FormattedMessage id="validity" />,
        accessorKey: "package_validity",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.package_validity}
          </Typography>
        ),
      },

      {
        header: () => <FormattedMessage id="Duration" />,
        accessorKey: "package_duration",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.package_duration}
          </Typography>
        ),
      },

      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: {
          className: "cell-center",
        },
        disableSortBy: true,
        cell: ({ row }) => {
          const isExpanded = expandedRowId === row.original.id;
          const collapseIcon = isExpanded ? (
            <CloseOutlined
              style={{
                color: theme.palette.error.main,
              }}
            />
          ) : (
            <EyeOutlined />
          );

          return (
            <Stack direction="row" spacing={0}>
              <Tooltip title={isExpanded ? "Collapse" : "View Details"}>
                <IconButton
                  color="secondary"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleRowExpand(row.original.id);
                  }}
                >
                  {collapseIcon}
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton
                  color="primary"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setSelectedCustomer(row.original);
                    setCustomerModal(true);
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleClose();
                    setSelected(row.original);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [theme, expandedRowId]
  );

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<VacancyPackage[]>([]);
  const [countPage, setCountPage] = useState({
    current_page: 0,
    last_page: 0,
  });
  const [currentPage, setCurrenttPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // const [searchedtext, setSearchedtext] = useState("");
  // const [loadingSearsh, setloadingSearsh] = useState(false);
  // const [searchQuery, setSearchQuery] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!selected?.id) return;
    setIsDeleting(true);
    try {
      await USERAXIOS.delete(`/vacancy_packages/${selected?.id}`);
      setResult((prevResults: any[]) =>
        prevResults.filter((item) => item.id !== selected?.id)
      );
      openSnackbar({
        open: true,
        message: "Vacancy packages deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        close: false,
        alert: {
          color: "success",
        },
      } as SnackbarProps);
      if (handleClose) handleClose();
    } catch (error: any) {
      openSnackbar({
        open: true,
        message:
          error?.response?.data?.message || "Failed to delete vacancy package",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setIsDeleting(false);
    }
  };

  //        ${searchQuery ? `&title=${searchQuery}` : ""}

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await USERAXIOS.get(
        `/vacancy_packages?page=${currentPage}`
      );
      const { data, meta } = response.data;
      setResult(data);
      setCountPage(meta);
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching vacancy packages",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    currentPage,
    // searchQuery
  ]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrenttPage(page);
  };

  // const handleSearch = () => {
  //   setloadingSearsh(true);
  //   setCurrenttPage(1);
  //   setSearchQuery(searchedtext);
  //   setloadingSearsh(false);
  // };

  const enhancedData = useMemo(() => {
    return result.map((item) => ({
      ...item,
      getCanExpand: () => true,
      getIsExpanded: () => expandedRowId === item.id,
      getToggleExpandedHandler: () => () => handleRowExpand(item.id),
    }));
  }, [result, expandedRowId]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <ReusableTable
          data={enhancedData}
          columns={columns}
          countPage={countPage}
          handlePageChange={handlePageChange}
          // handleSearchChange={handleSearch}
          currentPage={currentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setCurrenttPage={setCurrenttPage}
          // loadingSearsh={loadingSearsh}
          // searchedtext={searchedtext}
          // setSearchedtext={setSearchedtext}
          modalToggler={() => {
            setCustomerModal(true);
            setSelectedCustomer(null);
          }}
          renderDetailPanel={({ row }: any) => (
            <ExpandingDetail data={row.original} />
          )}
          expandedRowId={expandedRowId}
          setExpandedRowId={setExpandedRowId}
        />
      )}

      <ReusableDeleteDialog
        title={selected?.name}
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />

      <AddEditVacancyPackage
        open={customerModal}
        setResult={setResult}
        loading={formLoading}
        setLoading={setFormLoading}
        fetchData={fetchData}
        id={selected}
        vacancyPackage={selectedCustomer}
        handleClose={() => setCustomerModal(false)}
      />
    </>
  );
};

export default VacanciesPackages;
